import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet";
import Select from "react-select";
import { AuthContext } from "../../components/AuthContext";

const parameterOptions = [
  { value: "Door Type", label: "Door Type" },
  { value: "Thickness", label: "Thickness" },
  { value: "Size", label: "Door Size" },
];

const DoorsParameters = () => {
  const { user } = useContext(AuthContext);
  const [parameters, setParameters] = useState([]);
  const [formData, setFormData] = useState({
    parameterName: "",
    isThickness: false,
    parameterType: "",
    createdBy: user?.user?._id
  });
  const [isEditing, setIsEditing] = useState(false);
  const selectRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    fetchParameters();
  }, [currentPage, searchQuery]);

  const fetchParameters = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors/parameters?filter=${searchQuery}&limit=${itemsPerPage}&page=${currentPage}`);
      setParameters(response.data.parameters);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching parameters:", error);
      Swal.fire("Error", "Error fetching parameters", "error");
    }
  };

  const checkParameterInUse = async (parameterId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors`);
      const doors = response.data;
      
      return doors.some(door => 
        door.doorType === parameterId || 
        door.thickness === parameterId || 
        door.size === parameterId
      );
    } catch (error) {
      console.error("Error checking parameter usage:", error);
      return false;
    }
  };

  const validate = () => {
    let errors = {};
    if (!formData.parameterName) errors.parameterName = "Parameter Name is required";
    if (!formData.parameterType) errors.parameterType = "Parameter Type is required";
    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectChange = (selectedOption) => {
    setFormData({ 
      ...formData, 
      parameterType: selectedOption ? selectedOption.value : "",
      isThickness: selectedOption?.value !== "Door Type" ? false : formData.isThickness
    });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (param) => {
    const isInUse = await checkParameterInUse(param._id);
    
    if (isInUse) {
      Swal.fire('Cannot Delete', 'This parameter is currently in use and cannot be deleted.', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${param.parameterName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/doors/parameters/${param._id}`);
        Swal.fire('Deleted!', 'Parameter has been deleted.', 'success');
        fetchParameters();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete parameter', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      Swal.fire("Error", "Please fill in all required fields", "error");
      return;
    }

    try {
      const payload = {
        ...formData,
        isThickness: formData.parameterType === "Door Type" ? formData.isThickness : false
      };

      if (isEditing) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/doors/parameters/${formData._id}`, payload);
        Swal.fire("Success", "Parameter updated successfully", "success");
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/doors/parameters`, payload);
        Swal.fire("Success", "Parameter created successfully", "success");
      }
      
      fetchParameters();
      setFormData({ parameterName: "", isThickness: false, parameterType: formData.parameterType, createdBy: user?.user?._id });
      document.getElementById("parameterForm").reset();
      setIsEditing(false);
    } catch (error) {
      Swal.fire("Error", error.response?.data?.error || "An unexpected error occurred", "error");
      console.error("Error:", error);
    }
  };

  const handleUpdate = (param) => {
    setIsEditing(true);
    setFormData(param);
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: '#93c5fd'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
      color: state.isSelected ? 'white' : '#1f2937'
    })
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>Door Parameters | Door Management System</title>
      </Helmet>
      
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Door Parameters</h1>
          <input
            type="text"
            placeholder="Search Parameters"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
          />
        </div>

        <div className="bg-white shadow-xl rounded-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {isEditing ? 'Edit Parameter' : 'Add New Parameter'}
          </h2>
          
          <form id="parameterForm" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Parameter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="parameterName"
                  value={formData.parameterName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  placeholder="Enter parameter name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Parameter Type <span className="text-red-500">*</span>
                </label>
                <Select
                  options={parameterOptions}
                  value={parameterOptions.find(option => option.value === formData.parameterType)}
                  onChange={handleSelectChange}
                  isDisabled={isEditing}
                  ref={selectRef}
                  placeholder="Select Parameter Type"
                  styles={customSelectStyles}
                  required
                />
              </div>
            </div>

            {formData.parameterType === "Door Type" && (
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isThickness"
                  name="isThickness"
                  checked={formData.isThickness}
                  onChange={handleChange}
                  disabled={isEditing}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isThickness" className="text-sm font-medium text-gray-700">
                  Has Thickness Variation
                </label>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {isEditing ? "Update Parameter" : "Create Parameter"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ parameterName: "", isThickness: false, parameterType: "", createdBy: user?.user?._id });
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-600">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Parameter Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Thickness</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parameters.map((param) => (
                  <tr key={param.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{param.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.parameterName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.parameterType}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {param.parameterType === "Door Type" ? (param.isThickness ? "Yes" : "No") : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleUpdate(param)}
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(param)}
                        className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition duration-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {generatePageNumbers().map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoorsParameters;